import * as Sentry from '@sentry/react'

import { getErrorContext } from '@/shared/error-context/store'

export type UIErrorContext = {
	source:
		| 'component'
		| 'hook'
		| 'state'
		| 'router'
		| 'react-query'
		| 'unknown'
		| 'clerk'
	feature?: string
	component?: string
	action?: string
	section?: string
	entityId?: string
	metadata?: Record<string, string | number | boolean>
	/**
	 * Report this event even when the error is an `ApiError`.
	 *
	 * `beforeSend` in `app.tsx` drops every `ApiError` no `ErrorBoundary` tagged,
	 * on the rationale that the backend already reports its own failures. That
	 * rationale holds for a read the user retries by reloading; it does NOT hold
	 * for a write that fails, toasts, and is never retried — the toast tells the
	 * user, and nothing tells us. Set this on such a call site and `beforeSend`
	 * keeps the event (it becomes the `reportApiError` tag).
	 *
	 * Deliberately opt-IN, one call site at a time: the default filter is what
	 * keeps routine 401/403/404 noise out of Sentry, and flipping it wholesale
	 * would drown the events that matter. Reach for it on a write path whose
	 * failure nobody else reports, not on a read.
	 */
	reportApiError?: boolean
}

/**
 * Capture UI errors with context tags for Sentry grouping
 *
 * @example
 * captureException(error, {
 *   source: 'component',
 *   feature: 'alerts',
 *   component: 'AlertsTable',
 *   action: 'load_data'
 * })
 */
export function captureException(
	error: Error | unknown,
	context: UIErrorContext,
	level: Sentry.SeverityLevel = 'error',
): void {
	const errorObj = error instanceof Error ? error : new Error(String(error))

	// Auto-fill missing fields from error context store
	const storeContext = getErrorContext()
	const feature = context.feature ?? storeContext.feature
	const section = context.section ?? storeContext.section
	const entityId = context.entityId ?? storeContext.entityId
	const metadata = context.metadata ?? storeContext.metadata

	Sentry.withScope((scope) => {
		scope.setLevel(level)
		scope.setTag('error.source', context.source)
		scope.setTag('error.type', 'ui')

		if (feature) scope.setTag('feature', feature)
		if (context.component) scope.setTag('component', context.component)
		if (context.action) scope.setTag('action', context.action)
		if (section) scope.setTag('section', section)
		if (entityId) scope.setTag('entityId', entityId)
		// A tag, not a scope context, because `beforeSend` only sees the finished
		// event — `event.tags` is the one channel a call site has into that filter.
		if (context.reportApiError) scope.setTag('reportApiError', 'true')

		scope.setContext('ui_error', {
			...context,
			feature,
			section,
			entityId,
			metadata,
			path: window.location.pathname,
		})

		scope.setFingerprint([
			'ui-error',
			context.source,
			feature || 'unknown',
			errorObj.name,
		])

		Sentry.captureException(errorObj)
	})
}
